import React, {useCallback, useMemo} from 'react';
import {
    ChooseDropdown,
    TabMenuItem,
    TabsMenu,
    TabsSkinEnum,
    Text
} from '@priority-software/priority-style-react';
import {AutomationChoose} from '../../../../reducer/model';
import {texts} from '../../../../texts';
import {messageShortcuts, ruleDialogID} from '../../../../constants';
import {ColumnDropdownTrigger} from './ColumnDropdownTrigger';
import {isRTL} from '../../../../../../Localization';
import {getLanguageTabs} from './utils';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../../../utils/dataTestId';

type Props = {
    activeTabId: string;
    onTabChange: (id: string) => void;
    handleMessageData: (text: string) => void;
    columnsOptions: AutomationChoose[];
    isDisabledOldTemplate?: boolean;
    shouldShowTabs?: boolean;
};

export const AutomationActionToolbar = (props: Props) => {
    const {onTabChange, handleMessageData, columnsOptions, activeTabId, shouldShowTabs} = props;

    const languageTabs = useMemo(getLanguageTabs, []);

    const handleTabChange = useCallback(
        (id: string) => {
            onTabChange(id);
        },
        [onTabChange]
    );

    const onItemClick = useCallback(
        (text: string) => {
            handleMessageData(text);
        },
        [handleMessageData]
    );

    const handleFieldSelect = useCallback(
        (option: unknown) => {
            const {value} = option as {label: string; value: string};

            handleMessageData(value);
        },
        [handleMessageData]
    );

    const disabledValues = {
        disabled: props.isDisabledOldTemplate,
        color: props.isDisabledOldTemplate ? 'secondary-500' : 'primary-500',
        style: {cursor: props.isDisabledOldTemplate ? 'unset' : 'pointer'}
    } as const;

    return (
        <div className={styles.component} {...createDataTestId('message-compose-toolbar', false)}>
            {shouldShowTabs && (
                <div className={styles.leftToolbar}>
                    <TabsMenu skin={TabsSkinEnum.folder} bottomBorder={false}>
                        {languageTabs.map(tab => {
                            return (
                                <TabMenuItem
                                    key={tab.id}
                                    onTabClick={handleTabChange}
                                    isActive={activeTabId === tab.id}
                                    text={tab.title}
                                    id={tab.id}
                                />
                            );
                        })}
                    </TabsMenu>
                </div>
            )}

            <div className={styles.rightToolbar}>
                <ChooseDropdown
                    usePortal
                    hasSearch
                    value=""
                    openingSide="leftSide"
                    isRtl={isRTL()}
                    data={columnsOptions}
                    onChange={handleFieldSelect}
                    trigger={<ColumnDropdownTrigger {...{disabledValues}} />}
                    portalSelector={ruleDialogID}
                    componentClasses={styles.fieldDropdown}
                    contentComponentClasses={styles.fieldDropdownContent}
                    noOptionsText={texts.no_options_text}
                />

                <div className={styles.separator} />

                <Text
                    componentClasses={styles.link}
                    tag="span"
                    skin="breadcrumb"
                    textColor={disabledValues.color}
                    style={{...disabledValues.style}}
                    onClick={() => {
                        if (disabledValues.disabled) {
                            return;
                        }

                        onItemClick(
                            isRTL() ? texts.rule_number_label : messageShortcuts.ruleNumber
                        );
                    }}
                >
                    {texts.rule_number_label}
                </Text>

                <div className={styles.separator} />

                <Text
                    componentClasses={styles.link}
                    tag="span"
                    skin="breadcrumb"
                    textColor={disabledValues.color}
                    style={{...disabledValues.style}}
                    onClick={() => {
                        if (disabledValues.disabled) {
                            return;
                        }

                        onItemClick(
                            isRTL() ? texts.rule_description_label : messageShortcuts.ruleDesc
                        );
                    }}
                >
                    {texts.rule_description_label}
                </Text>
            </div>
        </div>
    );
};
